import type { LocalAppManifest, LocalCapability } from "./contracts";

const DATABASE_NAME = "browser-local-kernel-permissions";
const DATABASE_VERSION = 1;
const STORE_NAME = "app-permissions";

export type AppPermissionRecord = {
  schemaVersion: 1;
  appId: string;
  manifestVersion: string;
  storageNamespace: string;
  grantedCapabilities: readonly LocalCapability[];
  grantedAt: string;
  updatedAt: string;
};

export interface LocalPermissionStore {
  get(appId: string): Promise<AppPermissionRecord | undefined>;
  list(): Promise<AppPermissionRecord[]>;
  put(record: AppPermissionRecord): Promise<void>;
}

export class MemoryPermissionStore implements LocalPermissionStore {
  private readonly records = new Map<string, AppPermissionRecord>();

  async get(appId: string): Promise<AppPermissionRecord | undefined> {
    const record = this.records.get(appId);
    return record ? structuredClone(record) : undefined;
  }

  async list(): Promise<AppPermissionRecord[]> {
    return [...this.records.values()].map((record) => structuredClone(record));
  }

  async put(record: AppPermissionRecord): Promise<void> {
    this.records.set(record.appId, structuredClone(record));
  }
}

export class IndexedDbPermissionStore implements LocalPermissionStore {
  private constructor(private readonly database: IDBDatabase) {}

  static async open(): Promise<IndexedDbPermissionStore> {
    if (typeof indexedDB === "undefined") {
      throw new Error("IndexedDB is unavailable in this browser context.");
    }
    const database = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "appId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Permission database open failed."));
      request.onblocked = () => reject(new Error("Permission database upgrade is blocked."));
    });
    return new IndexedDbPermissionStore(database);
  }

  async get(appId: string): Promise<AppPermissionRecord | undefined> {
    return this.transaction("readonly", (store) => requestToPromise(store.get(appId)));
  }

  async list(): Promise<AppPermissionRecord[]> {
    return this.transaction("readonly", (store) => requestToPromise(store.getAll()));
  }

  async put(record: AppPermissionRecord): Promise<void> {
    await this.transaction("readwrite", (store) => requestToPromise(store.put(record)));
  }

  private transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const transaction = this.database.transaction(STORE_NAME, mode);
    return new Promise<T>((resolve, reject) => {
      let result: T;
      let operationFinished = false;
      transaction.oncomplete = () => {
        if (operationFinished) resolve(result);
      };
      transaction.onerror = () => reject(transaction.error ?? new Error("Permission transaction failed."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Permission transaction aborted."));
      void operation(transaction.objectStore(STORE_NAME))
        .then((value) => {
          result = value;
          operationFinished = true;
        })
        .catch(reject);
    });
  }
}

export function createPermissionRecord(app: LocalAppManifest, now = new Date().toISOString()): AppPermissionRecord {
  return {
    schemaVersion: 1,
    appId: app.id,
    manifestVersion: app.version,
    storageNamespace: app.storageNamespace,
    grantedCapabilities: [...app.requiredCapabilities],
    grantedAt: now,
    updatedAt: now,
  };
}

export function missingGrantedCapabilities(app: LocalAppManifest, record: AppPermissionRecord): LocalCapability[] {
  return app.requiredCapabilities.filter((capability) => !record.grantedCapabilities.includes(capability));
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Permission database request failed."));
  });
}
